import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmailStore } from '@/stores/useEmailStore';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import {
  EmailRichBodyEditor,
  buildHeroObject,
  interpolateTemplateText,
} from '@/components/email/EmailRichBodyEditor';
import { Loader2, Save, TestTube, Upload } from 'lucide-react';
import { toast } from 'sonner';

const SAMPLE_RECIPIENT = 'Alex';

const STUDIO_TEMPLATES = [
  {
    key: 'activation_confirmed',
    title: 'Activation confirmed',
    description:
      'B2B activation after hardware code redemption. Branded preview matches what Resend sends.',
    mergeVars: ['programName', 'programImageUrl'],
    samplePayload: {
      programName: 'Favvity Longevity: Balance Board Mastery',
      programImageUrl:
        'https://zfaucugicbitpmapnakj.supabase.co/storage/v1/object/public/Favvity/BALANCEBOARD_APP/BALANCEBOARD_APP_M04.jpg',
    },
    previewModes: null,
    heroImageOnly: true,
    publishNote: 'Published — activation worker uses this on the next send',
  },
  {
    key: 'subscription_welcome',
    title: 'Subscription welcome',
    description:
      'Stripe, Apple, and Google platform subscription welcome. Discovery copy for Anatomy 3D, Juniper, Garmin, and the exercise library.',
    mergeVars: [],
    samplePayload: {},
    previewModes: null,
    heroImageOnly: false,
    publishNote: 'Published — saved to DB; live welcome sender still uses stubs (Phase 2)',
  },
  {
    key: 'account_deleted',
    title: 'Account deleted',
    description:
      'Sent after GDPR account deletion (self-serve or admin). No App Store block or green CTA — confirmation and support reply only.',
    mergeVars: [],
    samplePayload: {},
    previewModes: null,
    heroImageOnly: false,
    publishNote: 'Published — delete-user-account uses published copy when Phase 2 is wired',
  },
  {
    key: 'password_recovery',
    title: 'Password recovery',
    description:
      'Supabase Auth password reset. Requires a signed reset URL at send time.',
    mergeVars: ['recoveryUrl'],
    samplePayload: {
      recoveryUrl: 'https://app.mymovement.space/reset-password?token=preview-sample',
    },
    previewModes: null,
    heroImageOnly: false,
    publishNote: 'Published — Auth reset sender uses published copy when Phase 2 is wired',
  },
];

function heroFromContent(hero, templateKey) {
  if (templateKey === 'activation_confirmed') {
    return {
      enabled: true,
      headline: '',
      accentWord: '',
      tagline: '',
      imageUrl: hero?.imageUrl || '{{programImageUrl}}',
    };
  }

  return {
    enabled: Boolean(hero?.headline?.trim() || hero?.imageUrl?.trim()),
    headline: hero?.headline || '',
    accentWord: hero?.accentWord || '',
    tagline: hero?.tagline || '',
    imageUrl: hero?.imageUrl || '',
  };
}

function TemplateEditor({ config }) {
  const {
    templateContent,
    templateContentLoading,
    templateSaving,
    templatePublishing,
    fetchTemplateContent,
    saveTemplateDraft,
    publishTemplate,
    sendTemplateTest,
    templateTestSending,
  } = useEmailStore();
  const { can } = useAuth();
  const canManageTemplates = can('email.templates.manage');

  const [locale, setLocale] = useState('de');
  const [previewMode, setPreviewMode] = useState(
    config.previewModes?.[0]?.id ?? 'default'
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [heroEnabled, setHeroEnabled] = useState(config.key === 'activation_confirmed');
  const [heroHeadline, setHeroHeadline] = useState('');
  const [heroAccentWord, setHeroAccentWord] = useState('');
  const [heroTagline, setHeroTagline] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState(
    config.key === 'activation_confirmed' ? '{{programImageUrl}}' : ''
  );
  const [videos, setVideos] = useState([]);

  const samplePayload = useMemo(() => {
    if (!config.previewModes) return config.samplePayload;
    return (
      config.previewModes.find((mode) => mode.id === previewMode)?.payload ??
      config.samplePayload
    );
  }, [config.previewModes, config.samplePayload, previewMode]);

  const loadContent = useCallback(async () => {
    try {
      const content = await fetchTemplateContent({ templateKey: config.key, locale });
      setSubject(content?.draftSubject || '');
      setBody(content?.draftBodyHtml || '');
      const hero = heroFromContent(content?.draftHero, config.key);
      setHeroEnabled(hero.enabled);
      setHeroHeadline(hero.headline);
      setHeroAccentWord(hero.accentWord);
      setHeroTagline(hero.tagline);
      setHeroImageUrl(hero.imageUrl);
      setVideos(content?.draftVideos || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load template');
    }
  }, [config.key, fetchTemplateContent, locale]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const draftPayload = useMemo(
    () => ({
      templateKey: config.key,
      locale,
      subject: subject.trim(),
      bodyHtml: body.trim(),
      hero: buildHeroObject({
        enabled: heroEnabled,
        headline: heroHeadline,
        accentWord: heroAccentWord,
        tagline: heroTagline,
        imageUrl: heroImageUrl,
      }),
      videos,
    }),
    [
      config.key,
      locale,
      subject,
      body,
      heroEnabled,
      heroHeadline,
      heroAccentWord,
      heroTagline,
      heroImageUrl,
      videos,
    ]
  );

  const publishedLabel = useMemo(() => {
    const v = templateContent?.publishedVersion ?? 0;
    if (v <= 0) return 'Not published yet';
    return `Published v${v}`;
  }, [templateContent]);

  const handleSaveDraft = async () => {
    if (!draftPayload.subject || !draftPayload.bodyHtml) {
      toast.error('Subject and body are required');
      return;
    }
    try {
      await saveTemplateDraft(draftPayload);
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };

  const handleTestSend = async (useDraft = true) => {
    if (useDraft && (!draftPayload.subject || !draftPayload.bodyHtml)) {
      toast.error('Subject and body are required');
      return;
    }
    if (!useDraft && (templateContent?.publishedVersion ?? 0) <= 0) {
      toast.error('Publish first to test the published version');
      return;
    }

    try {
      await sendTemplateTest({
        ...draftPayload,
        payload: samplePayload,
        recipientName: SAMPLE_RECIPIENT,
        useDraft,
      });
      toast.success('Test email sent to your inbox');
    } catch (err) {
      toast.error(err.message || 'Test send failed');
    }
  };

  const handlePublish = async () => {
    try {
      await saveTemplateDraft(draftPayload);
      await publishTemplate({ templateKey: config.key, locale });
      toast.success(config.publishNote);
      await loadContent();
    } catch (err) {
      toast.error(err.message || 'Publish failed');
    }
  };

  const previewSubject = interpolateTemplateText(subject, samplePayload);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{config.title}</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">{config.description}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{config.key}</Badge>
          <Badge variant="outline">{publishedLabel}</Badge>
        </div>

        <p className="text-sm text-muted-foreground">
          Merge vars:{' '}
          {config.mergeVars.length > 0 ? (
            config.mergeVars.map((name) => (
              <code key={name} className="text-xs mr-2">
                {`{{${name}}}`}
              </code>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">none</span>
          )}
        </p>

        {config.previewModes ? (
          <div className="space-y-2">
            <Label>Preview / test payload</Label>
            <div className="flex gap-2">
              {config.previewModes.map((mode) => (
                <Button
                  key={mode.id}
                  type="button"
                  variant={previewMode === mode.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewMode(mode.id)}
                >
                  {mode.label}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label>Locale</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={locale === 'de' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setLocale('de')}
            >
              Deutsch
            </Button>
            <Button
              type="button"
              variant={locale === 'en' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setLocale('en')}
            >
              English
            </Button>
          </div>
        </div>

        {templateContentLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            <EmailRichBodyEditor
              subject={subject}
              onSubjectChange={setSubject}
              body={body}
              onBodyChange={setBody}
              language={locale}
              onLanguageChange={setLocale}
              heroEnabled={heroEnabled}
              onHeroEnabledChange={setHeroEnabled}
              heroHeadline={heroHeadline}
              onHeroHeadlineChange={setHeroHeadline}
              heroAccentWord={heroAccentWord}
              onHeroAccentWordChange={setHeroAccentWord}
              heroTagline={heroTagline}
              onHeroTaglineChange={setHeroTagline}
              heroImageUrl={heroImageUrl}
              onHeroImageUrlChange={setHeroImageUrl}
              videos={videos}
              onVideosChange={setVideos}
              previewPayload={samplePayload}
              previewRecipientName={SAMPLE_RECIPIENT}
              showLanguage={false}
              heroImageOnly={config.heroImageOnly}
            />

            <div className="flex flex-wrap gap-2">
              {canManageTemplates && (
              <Button type="button" onClick={handleSaveDraft} disabled={templateSaving}>
                {templateSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save draft
              </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => handleTestSend(true)}
                disabled={templateTestSending}
              >
                {templateTestSending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <TestTube className="h-4 w-4 mr-2" />
                )}
                Test send (draft)
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleTestSend(false)}
                disabled={
                  templateTestSending || (templateContent?.publishedVersion ?? 0) <= 0
                }
              >
                Test send (published)
              </Button>
              {canManageTemplates && (
              <Button type="button" onClick={handlePublish} disabled={templatePublishing}>
                {templatePublishing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Publish
              </Button>
              )}
            </div>

            {previewSubject ? (
              <p className="text-sm text-muted-foreground">
                Sample subject:{' '}
                <span className="text-foreground">{previewSubject}</span>
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function EmailTemplatesPanel() {
  const [activeTemplateKey, setActiveTemplateKey] = useState(STUDIO_TEMPLATES[0].key);
  const activeConfig =
    STUDIO_TEMPLATES.find((template) => template.key === activeTemplateKey) ??
    STUDIO_TEMPLATES[0];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Template</Label>
        <div className="flex flex-wrap gap-2">
          {STUDIO_TEMPLATES.map((template) => (
            <Button
              key={template.key}
              type="button"
              variant={activeTemplateKey === template.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTemplateKey(template.key)}
            >
              {template.title}
            </Button>
          ))}
        </div>
      </div>

      <TemplateEditor key={activeTemplateKey} config={activeConfig} />
    </div>
  );
}
